import Login from "./interfaces/Login";
import axios, { AxiosError, AxiosInstance } from "axios";
import Result, { IError } from "./helpers/result";
import IClientSession from "./interfaces/ClientSession";
import { setClientSessionDates } from "./helpers/clientSessionHelpers"
import { useAuthStore } from "./store/authStore";
import User from "./interfaces/User";
import { Pinia } from "pinia";
import Cookies from 'js-cookie'

class HakosaloAuth {
	localStorageKey = "hakosalo_session";
	myAxios;
	myPinia = null as null|Pinia;

	constructor() {
		this.myAxios = axios.create();
	}

	setBaseUrl(baseUrl:string) {
		this.myAxios = axios.create({
			baseURL: baseUrl
		});
	}

	setPinia(pinia:Pinia) {
		this.myPinia = pinia;
	}

	get authStore() {
		return useAuthStore(this.myPinia);
	}

	setSession(session : IClientSession) {
		const sessionJsonString = JSON.stringify(session);
		window.localStorage.setItem(this.localStorageKey, sessionJsonString);
		this.setSessionForCookie(session);
	}

	setSessionForCookie(session:IClientSession) {
		const sessionString = this.generateAuthorizationHeaderValue(session);
		Cookies.set(this.localStorageKey, sessionString, { sameSite: "strict"});
	}

	/**
	 * Get Session Object from Local Storage (parsed).
	 * @returns ClientSession object or null, if not found
	 */
	getSessionFromLocalStorage() : IClientSession|null {
		// Check if session data exists 
		const sessionJson = window.localStorage.getItem(this.localStorageKey);
		if (sessionJson == null) {
			return null;
		}

		const session = JSON.parse(sessionJson) as IClientSession;
		setClientSessionDates(session);

		// This sets the cookie 
		this.setSessionForCookie(session);
		return session;
	}

	/**
	 * Hakosalo Login. On succecsful login, saves session to LocalStorage.
	 * @param model Organization, username and password
	 * @returns Result<ClientSession>
	 */
	async loginAsync(model:Login) : Promise<Result<IClientSession>> {
		try {
			// Try to login
			const response = await this.myAxios.post("auth/login", model);

			// At this point the login is succesful.
			// Save Session Data to LocalStorage.
			this.setSession(response.data);
			// window.localStorage.setItem(this.localStorageKey, JSON.stringify(response.data))

			// Get user data 
			const user = await this.getUserAsync();

			// Unexpected error
			if (user == null) {
				return new Result<IClientSession>().failure([{codeName: "user_null"}]);
			}

			// Save session to store 
			// this.authStore.session = response.data;
			this.authStore.user = user;

			// Return session data
			return new Result<IClientSession>().success(response.data);
		} 
		catch(ex:unknown) {
			// Login failed. If server knows why, return that.
			const axiosErr = ex as AxiosError;
			if (!axiosErr.response || !axiosErr.response.data) {
				return new Result<IClientSession>().failure([{ codeName: "unknown_error" }]);
			}
			
			const errData = axiosErr.response.data as IError[];
			return new Result<IClientSession>().failure(errData);
		}
	}

	async logoutAsync() : Promise<boolean> {
		try {
			const session = this.authStore.session as IClientSession;
			await this.myAxios.get("Auth/Logout", {
				headers: {
					authorization: this.generateAuthorizationHeaderValue(session)
				}
			});
			window.localStorage.removeItem(this.localStorageKey);
			this.authStore.user = null;
			return true;
		}
		catch {
			return false;
		}
	}

	async getUserAsync() : Promise<User|null> {
		try {
			const session = this.getSessionFromLocalStorage();
			if (session == null) {
				return null;
			}

			const response = await this.myAxios.get("Auth/Me", {
				headers: {
					authorization: this.generateAuthorizationHeaderValue(session)
				}
			});

			const user = response.data as User;
			return user;
		}
		catch(ex:unknown) {
			//const axiosErr = ex as AxiosError;
			return null;
		}
	}

	/**
	 * Generate Authorization Header Value
	 * @param session Client Session Object
	 * @returns Authorization Header Value
	 */
	generateAuthorizationHeaderValue(session:IClientSession) 
	{
		return session.orgIdentifier + "|" + session.token;
	}

	/**
	 * Fetch session data with 1-time token 
	 */
	async getSessionWithOneTimeTokenAsync(fetchToken:string) {
		try {
			const response = await this.myAxios.get("Auth/GetSessionWithOneTimeToken/" + fetchToken);
			const session = response.data as IClientSession;
			
			this.setSession(session);
			// window.localStorage.setItem(this.localStorageKey, JSON.stringify(session));
			return session;
		}
		catch(ex:unknown) {
			console.warn("Cannot get session with one time fetch token");
			console.warn(ex);
			return null;
		}
	}

	/**
	 * Generate a new 1-time token
	 */
	async createOneTimeTokenAsync() {
		const existingSession = this.getSessionFromLocalStorage();

		if (!existingSession) {
			return null;
		}
		
		try {
			const response = await this.myAxios.get("Auth/CreateOneTimeToken", {
				headers: {
					authorization: this.generateAuthorizationHeaderValue(existingSession)
				}
			});
			const fetchToken = response.data as string;

			return fetchToken;
		} catch {
			return null;
		}
	}

	/**
	 * Use this function before app starts. Checks for existing session and
	 * setups authStore accordingly.
	 */
	async onStartAsync() : Promise<boolean> {
		const queryString = new URLSearchParams(window.location.search);

		const fetchToken = queryString.get("auth-ft") || "";

		let session = null as null | IClientSession;
		if (fetchToken) {
			session = await this.getSessionWithOneTimeTokenAsync(fetchToken);
			if (session != null) {
				this.setSession(session);
			}

			this.removeFetchTokenFromUrl();
		}

		session = this.getSessionFromLocalStorage();

		if (session == null) {
			return false;
		}

		const currentDate = new Date();
		if (currentDate > session.expiresAt) {
			return false;
		}

		const user = await this.getUserAsync();

		if (user == null) 
		{
			return false;
		}

		// this.authStore.session = session;
		this.authStore.user = user;

		return true;
	}

	removeFetchTokenFromUrl()
	{
		const queryString = new URLSearchParams(window.location.search);
		queryString.delete("auth-ft");
		const loc = window.location;
		let urlWithoutFt = loc.origin + loc.pathname;
		const qsStr = queryString.toString();
		if (qsStr) {
			urlWithoutFt = "?" + queryString.toString();
		}

		window.history.replaceState({}, document.title, urlWithoutFt);
	}
}

let hakosaloAuthInstance = new HakosaloAuth();

/**
 * Call after Pinia has been created.
 * @param baseurl Login Service API URL
 */
const createHakosaloAuth = (baseurl:string|null) => {
	hakosaloAuthInstance = new HakosaloAuth();
	if (baseurl) {
		hakosaloAuthInstance.setBaseUrl(baseurl);
	}
}

export default hakosaloAuthInstance;
export {
	HakosaloAuth,
	createHakosaloAuth
}